/* eslint-disable no-unused-vars */
import apiService from '@/utils/api-client'
import config from '@/utils/appConfig'
import user from '@/store/modules/user'
import { featureAccess } from '@/utils/accesses'
import { setOptionsToChipsFilters } from '@/utils/entity'

export default {
  async getCountries() {
    try {
      return await apiService.get('https://restcountries.com/v2/all')
    } catch (error) {
      console.error('Error fetching countries:', error)
    }
  },
  async getLanguages() {
    try {
      const response = await apiService.get(
        'https://restcountries.com/v3.1/all',
      )

      return response || []
    } catch (error) {
      console.error('Error fetching languages:', error)
    }
  },

  // eslint-disable-next-line no-unused-vars
  async getEntityList(
    { commit },
    {
      ids,
      customProject = {},
      configParams = {},
      searchAutocomplete,
      $limit,
      $skip,
      filterTypeId,
      filterTagIds,
      score,
      name,
      names,
      aliasName,
      includeLogo,
      domicileCountry,
      primaryOperationalRegions,
      restrictedJurisdictions,
      licensedCountry,
      privacyCoin,
      status,
      KYCLevel,
      popular = false,
    },
  ) {
    try {
      if (featureAccess('ENTITY')) {
        const params = { $limit, $skip }

        if (name) {
          params['name'] = `${name}`
        }
        if (ids) {
          params['ids'] = ids
        }
        if (names) {
          params['name'] = names
        }
        if (score) {
          params['score'] = score
        }
        if (filterTagIds) {
          params['tags'] = filterTagIds
        }
        if (filterTypeId) {
          params['type'] = filterTypeId
        }
        if (domicileCountry) {
          params['data.domicileCountry'] = domicileCountry
        }
        if (primaryOperationalRegions) {
          params['data.primaryOperationalRegions'] = primaryOperationalRegions
        }
        if (restrictedJurisdictions) {
          params['data.restrictedJurisdictions'] = restrictedJurisdictions
        }
        if (licensedCountry) {
          params['data.regulatoryCompliance.country'] = licensedCountry
        }
        if (privacyCoin != null) {
          params['data.isPrivacyCoinsSupported'] = privacyCoin
        }
        if (status) {
          params['data.status'] = status
        }
        if (KYCLevel) {
          params['data.KYCStatus'] = KYCLevel
        }

        if (searchAutocomplete) {
          params['name'] = `.*${searchAutocomplete}.*`
        }
        if (aliasName) {
          params['alias.value'] = `.*${aliasName}.*`
        }

        if (popular) {
          params['popular'] = popular
        }

        const projectFields = {
          name: 1,
          type: 1,
          id: 1,
          ...customProject,
        }

        if (includeLogo) {
          projectFields.logo = 1
        }

        if (user.getters.hasEntityAccess(user.state)) {
          return apiService.get(`/entity`, {
            params: {
              ...params,
              $project: projectFields,
            },
            cancelToken: configParams.cancelToken,
            baseURL: config.VUE_APP_LABEL_API_URL,
          })
        } else {
          return apiService.get(`/entity/list`, {
            params: {
              ...params,
              ids,
              name: Array.isArray(names) ? names : name ? [name] : [],
            },
            cancelToken: configParams.cancelToken,
            baseURL: config.VUE_APP_LABEL_API_URL,
          })
        }
      }
    } catch (error) {
      console.error(error)
    }
  },
  // eslint-disable-next-line no-unused-vars
  async getEntityById({ commit }, { id, ids, screenshots }) {
    try {
      const params = {}
      if (ids) {
        params['ids'] = [ids]
        params['$project[data][screenshots]'] = screenshots
      }
      if (id) {
        params['id'] = id
      }

      const data = apiService.get('/entity', {
        params,
        baseURL: config.VUE_APP_LABEL_API_URL,
      })

      return data
    } catch (error) {
      console.error(error)
    }
  },
  // eslint-disable-next-line no-unused-vars
  async createEntity({ commit }, { name, type, tags, data, alias, aliasVisible, logo }) {
    try {
      return apiService.post(
        `/entity`,
        { name, type, tags, data, alias, aliasVisible, logo },
        { baseURL: config.VUE_APP_LABEL_API_URL },
      )
    } catch (error) {
      console.error(error)
    }
  },
  // eslint-disable-next-line no-unused-vars
  async editEntity({ commit }, { name, type, tags, data, id, alias, aliasVisible, logo }) {
    try {
      // PUT /entity/:id replaces the whole document server-side, so every
      // top-level field has to travel with it or it is dropped from the entity
      return apiService.put(
        `/entity/${id}`,
        { name, type, tags, data, alias, aliasVisible, logo },
        { baseURL: config.VUE_APP_LABEL_API_URL },
      )
    } catch (error) {
      console.error(error)
    }
  },
  // eslint-disable-next-line no-unused-vars
  async deleteEntity({ commit }, id) {
    try {
      const data = apiService.delete(`/entity/${id}`, {
        baseURL: config.VUE_APP_LABEL_API_URL,
      })
      return data
    } catch (error) {
      console.error(error)
    }
  },
  // an environment where the regulator data fix has not run answers with an
  // empty list; the dropdown has to stay usable either way
  async getEntityRegulators({ commit }) {
    try {
      const data = await apiService.get('/entity/regulator', {
        baseURL: config.VUE_APP_LABEL_API_URL,
      })

      commit('SET_ENTITY_REGULATORS_LIST', data?.items || [])
    } catch (error) {
      console.error(error)
      commit('SET_ENTITY_REGULATORS_LIST', [])
    }
  },
  async getEntityMeta({ commit }, { item }) {
    try {
      const params = {}
      if (item) {
        params['item'] = item
      }

      const data = await apiService.get('/entity-meta', {
        params,
        baseURL: config.VUE_APP_LABEL_API_URL,
      })

      if (item == 'payment-system') {
        commit('SET_ENTITY_PAYMENT_SERVICE_LIST', data)
      } else if (item == 'payment-method') {
        commit('SET_ENTITY_PAYMENT_METHOD_LIST', data)
      } else if (item == 'provided-service') {
        commit('SET_ENTITY_PROVIDED_SERVICE_LIST', data)
      } else if (item == 'banks') {
        commit('SET_ENTITY_BANKS_LIST', data)
      }
    } catch (error) {
      console.error(error)
    }
  },
  async createEntityMeta(cntx, { item, meta }) {
    try {
      return apiService.post(
        `/entity-meta`,
        { item, ...meta },
        { baseURL: config.VUE_APP_LABEL_API_URL },
      )
    } catch (error) {
      console.error(error)
    }
  },
  updateTypeOptionsChipsFilters({ commit, getters }) {
    const transformed = setOptionsToChipsFilters({
      getters,
      key: 'type',
      options: getters.getEntityTypesList || [],
    })
    commit('SET_CHIPS_FILTERS', transformed)
  },
  updateTagsOptionsChipsFilters({ commit, getters }) {
    const transformed = setOptionsToChipsFilters({
      getters,
      key: 'tags',
      options: getters.getEntityTagsList || [],
    })
    commit('SET_CHIPS_FILTERS', transformed)
  },
  updateDomiciledOptionsChipsFilters({ commit, getters }) {
    const transformed = setOptionsToChipsFilters({
      getters,
      key: 'domiciled',
      options: getters.getEntityCountriesList || [],
    })
    commit('SET_CHIPS_FILTERS', transformed)
  },
  updatePrimaryRegionsOptionsChipsFilters({ commit, getters }) {
    const transformed = setOptionsToChipsFilters({
      getters,
      key: 'primary_regions',
      options: getters.getEntityCountriesList || [],
    })
    commit('SET_CHIPS_FILTERS', transformed)
  },
  updateRestrictedJurisdictionsOptionsChipsFilters({ commit, getters }) {
    const transformed = setOptionsToChipsFilters({
      getters,
      key: 'restricted_jurisdictions',
      options: getters.getEntityCountriesList || [],
    })
    commit('SET_CHIPS_FILTERS', transformed)
  },
  updateLicensedCountryOptionsChipsFilters({ commit, getters }) {
    const transformed = setOptionsToChipsFilters({
      getters,
      key: 'licensed_country',
      options: getters.getEntityCountriesList || [],
    })
    commit('SET_CHIPS_FILTERS', transformed)
  },
  updateAllOptionsChipsFilters({ dispatch }) {
    dispatch('updateTypeOptionsChipsFilters')
    dispatch('updateTagsOptionsChipsFilters')
    dispatch('updateDomiciledOptionsChipsFilters')
    dispatch('updatePrimaryRegionsOptionsChipsFilters')
    dispatch('updateRestrictedJurisdictionsOptionsChipsFilters')
    dispatch('updateLicensedCountryOptionsChipsFilters')
  },
  clearAllChipsFilterValues({ commit, getters }) {
    const cleared = getters.getChipsFilters.map(f => ({ ...f, value: [] }))
    commit('SET_CHIPS_FILTERS', cleared)
  },
}
